import { useEffect, useRef, useState } from "react"
import { CheckCircle2, Loader2, Play, Trophy, X, XCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, type SelectOption } from "@/components/ui/select"
import { cn } from "@/lib/cn"
import type { EscalationPolicy } from "@/types/escalation_policy"

// "Test rules" modal — replaces the per-rule sticky panel. Pick an
// (appuser, convo) pair and the BE evaluates every rule in the active
// policy set in `position` order; the response flags the first-match
// winner. Selection persists in localStorage so the operator can
// re-open the modal later without retyping.
//
// Backed by:
//   POST /escalation_policies/preview_all_matches
//   GET  /appusers/search?q=...
//   GET  /appusers/:id/recent_conversations?limit=20

const STORAGE_KEY = "valet.testRule.lastPair"

type DimensionStatus = "pass" | "fail" | "skipped"

type RuleResult = {
  policy_id:  number
  position:   number
  name:       string
  active:     boolean
  matched:    boolean
  dimensions: {
    reasons:       DimensionStatus
    topics:        DimensionStatus
    recent:        DimensionStatus
    custom_fields: DimensionStatus
  }
}

type PreviewAllResponse = {
  winnerId: number | null
  results:  RuleResult[]
}

type AppuserHit = {
  id:    number
  label: string
}

type ConvoOption = {
  id:    number
  label: string
}

type Props = {
  policies:        EscalationPolicy[]
  // Currently-edited rule. Highlighted in the result list so operators
  // can spot "this is the rule I'm working on" at a glance.
  selectedPolicyId: number
  onClose:          () => void
}

export function TestRuleModal({ policies, selectedPolicyId, onClose }: Props) {
  const [appuser, setAppuser] = useState<AppuserHit | null>(null)
  const [convos, setConvos] = useState<ConvoOption[]>([])
  const [convoId, setConvoId] = useState<number | null>(null)
  const [appuserSearch, setAppuserSearch] = useState("")
  const [appuserHits, setAppuserHits] = useState<AppuserHit[]>([])
  const [searching, setSearching] = useState(false)
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<PreviewAllResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Rehydrate the most recent pair on first open.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw) as { appuser: AppuserHit | null; convoId: number | null }
      if (parsed.appuser) setAppuser(parsed.appuser)
      if (typeof parsed.convoId === "number") setConvoId(parsed.convoId)
    } catch {
      try { localStorage.removeItem(STORAGE_KEY) } catch { /* noop */ }
    }
  }, [])

  // Persist on every change.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ appuser, convoId }))
    } catch {
      // noop — storage may be disabled in some browsers
    }
  }, [appuser, convoId])

  // Debounced typeahead. Aborts the previous request on each keystroke.
  const searchAbortRef = useRef<AbortController | null>(null)
  useEffect(() => {
    const q = appuserSearch.trim()
    if (q.length < 2) { setAppuserHits([]); return }
    setSearching(true)
    searchAbortRef.current?.abort()
    const ac = new AbortController()
    searchAbortRef.current = ac
    const handle = setTimeout(async () => {
      try {
        const res = await fetch(`/appusers/search?q=${encodeURIComponent(q)}`, {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
          signal: ac.signal,
        })
        if (!res.ok) { setAppuserHits([]); return }
        const json = await res.json() as { results?: AppuserHit[] }
        setAppuserHits(Array.isArray(json.results) ? json.results : [])
      } catch (e) {
        if ((e as Error).name === "AbortError") return
        setAppuserHits([])
      } finally {
        setSearching(false)
      }
    }, 200)
    return () => { clearTimeout(handle); ac.abort() }
  }, [appuserSearch])

  // Refresh convo dropdown whenever the appuser changes.
  useEffect(() => {
    if (!appuser) { setConvos([]); return }
    let ignore = false
    fetch(`/appusers/${appuser.id}/recent_conversations?limit=20`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then((res) => res.ok ? res.json() : { conversations: [] })
      .then((json: { conversations?: ConvoOption[] }) => {
        if (ignore) return
        const list = Array.isArray(json.conversations) ? json.conversations : []
        setConvos(list)
        if (!list.some((c) => c.id === convoId)) {
          setConvoId(list[0]?.id ?? null)
        }
      })
      .catch(() => { if (!ignore) setConvos([]) })
    return () => { ignore = true }
  }, [appuser]) // eslint-disable-line react-hooks/exhaustive-deps

  const canRun = !!appuser && !!convoId && !running
  const run = async () => {
    if (!canRun) return
    setRunning(true)
    setError(null)
    setResult(null)
    try {
      const csrf = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content ?? ""
      const res = await fetch("/escalation_policies/preview_all_matches", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token":  csrf,
          "Content-Type":  "application/json",
          "Accept":        "application/json",
        },
        body: JSON.stringify({ appuser_id: appuser!.id, convo_id: convoId! }),
      })
      if (!res.ok) {
        const txt = await res.text().catch(() => "")
        setError(`Preview failed (${res.status}). ${txt}`.trim())
        return
      }
      const json = await res.json() as PreviewAllResponse
      setResult(json)
    } catch (e) {
      setError(e instanceof Error ? e.message : "Preview failed")
    } finally {
      setRunning(false)
    }
  }

  // Auto-run if the persisted pair survived rehydration. The operator
  // who opened the modal yesterday wants to see the verdict immediately,
  // not click Run a second time.
  const ranOnceRef = useRef(false)
  useEffect(() => {
    if (ranOnceRef.current) return
    if (!appuser || !convoId) return
    ranOnceRef.current = true
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appuser, convoId])

  // Esc-to-close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 py-8">
      <div className="flex max-h-full w-full max-w-3xl flex-col overflow-hidden rounded-card border border-border bg-app-panel shadow-card">
        {/* Header */}
        <header className="flex items-start justify-between gap-3 border-b border-border px-5 py-3">
          <div>
            <div className="text-base font-semibold text-app-text">Test rules</div>
            <div className="text-[11px] text-app-muted">
              Pick an appuser + conversation, evaluate every rule in the active set, and see which one wins by position.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-control p-1 text-app-muted hover:bg-white/5 hover:text-app-text"
          >
            <X size={18} />
          </button>
        </header>

        {/* Pair pickers */}
        <div className="grid gap-3 border-b border-border px-5 py-3 sm:grid-cols-[1fr_1fr_auto]">
          <AppuserCombobox
            value={appuser}
            search={appuserSearch}
            onSearch={setAppuserSearch}
            options={appuserHits}
            searching={searching}
            onPick={(hit) => {
              setAppuser(hit)
              setAppuserSearch("")
              setAppuserHits([])
              ranOnceRef.current = false
            }}
          />
          <Select<number>
            value={
              convos.find((c) => c.id === convoId)
                ? { value: convoId!, label: convos.find((c) => c.id === convoId)!.label }
                : null
            }
            onChange={(o) => {
              setConvoId(o ? o.value : null)
              ranOnceRef.current = false
            }}
            options={convos.map((c): SelectOption<number> => ({ value: c.id, label: c.label }))}
            isDisabled={!appuser}
            placeholder={
              !appuser ? "Pick an appuser first"
              : convos.length === 0 ? "No recent conversations"
              : "Pick a conversation…"
            }
          />
          <Button
            variant="red"
            size="sm"
            onClick={run}
            disabled={!canRun}
            title={!appuser ? "Pick an appuser" : !convoId ? "Pick a conversation" : "Run match"}
          >
            {running ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
            <span className="ml-1.5">Run</span>
          </Button>
        </div>

        {/* Body — results */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error && (
            <div className="rounded-control border border-status-error/40 bg-status-error/10 px-3 py-2 text-[11px] text-status-error">
              {error}
            </div>
          )}

          {!result && !error && (
            <div className="px-1 py-6 text-center text-xs text-app-muted">
              {!appuser ? "Pick an appuser to begin." :
               !convoId ? "Pick a conversation to evaluate." :
               running ? "Evaluating…" : "Click Run to evaluate every rule."}
            </div>
          )}

          {result && result.results.length === 0 && (
            <div className="px-1 py-6 text-center text-xs text-app-muted">
              No rules in the active set.
            </div>
          )}

          {result && result.results.length > 0 && (
            <div className="space-y-1.5">
              {result.results.map((r) => (
                <RuleRow
                  key={r.policy_id}
                  rule={r}
                  totalPolicies={policies.length}
                  isWinner={r.policy_id === result.winnerId}
                  isCurrent={r.policy_id === selectedPolicyId}
                />
              ))}
              <WinnerSummary winner={result.results.find((r) => r.policy_id === result.winnerId) ?? null} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function RuleRow({
  rule, totalPolicies, isWinner, isCurrent,
}: {
  rule: RuleResult
  totalPolicies: number
  isWinner: boolean
  isCurrent: boolean
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-card border px-3 py-2 transition",
        isWinner
          ? "border-vip-gold/60 bg-vip-goldMute/40"
          : rule.matched
            ? "border-status-success/30 bg-status-success/5"
            : "border-border bg-white/[0.02]",
        !rule.active && "opacity-50",
      )}
    >
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-control bg-white/[0.04] text-[11px] font-semibold tabular-nums text-app-muted">
        {rule.position + 1}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate text-sm font-medium text-app-text">{rule.name}</span>
          {isCurrent && (
            <span className="rounded-chip border border-brand-red/40 bg-brand-redSoft px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-brand-red">
              editing
            </span>
          )}
          {!rule.active && (
            <span className="rounded-chip border border-border bg-white/5 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-app-subtle">
              inactive
            </span>
          )}
          {isWinner && (
            <span className="ml-auto inline-flex items-center gap-1 rounded-chip border border-vip-gold/50 bg-vip-gold/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-vip-goldSoft">
              <Trophy size={10} /> Wins
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-app-muted">
          <DimChip label="reasons"  status={rule.dimensions.reasons}  />
          <DimChip label="topics"   status={rule.dimensions.topics}   />
          <DimChip label="recent"   status={rule.dimensions.recent}   />
          <DimChip label="fields"   status={rule.dimensions.custom_fields} />
        </div>
      </div>

      <div className="shrink-0">
        {rule.matched
          ? <CheckCircle2 size={18} className="text-status-success" />
          : <XCircle size={18} className="text-status-error/70" />}
      </div>

      {/* totalPolicies kept in scope for future "rule i/N" display */}
      <span className="hidden">{totalPolicies}</span>
    </div>
  )
}

function DimChip({ label, status }: { label: string; status: DimensionStatus }) {
  const tone =
    status === "pass"    ? "text-status-success" :
    status === "fail"    ? "text-status-error/80" :
                           "text-app-subtle"
  const glyph =
    status === "pass"    ? "✓" :
    status === "fail"    ? "✗" :
                           "·"
  return (
    <span className={cn("inline-flex items-center gap-0.5", tone)}>
      <span>{glyph}</span>
      <span className="lowercase">{label}</span>
    </span>
  )
}

function WinnerSummary({ winner }: { winner: RuleResult | null }) {
  if (!winner) {
    return (
      <div className="mt-3 rounded-control border border-status-warning/40 bg-status-warning/10 px-3 py-2 text-[11px] text-status-warning">
        No active rule matched. Triage will fall through to the company default.
      </div>
    )
  }
  return (
    <div className="mt-3 rounded-control border border-vip-gold/40 bg-vip-goldMute/30 px-3 py-2 text-[11px] text-app-text">
      <strong>Rule #{winner.position + 1}</strong> — <em>{winner.name}</em> wins by position. First match in <code>position ASC</code> with all dimensions passing.
    </div>
  )
}

function AppuserCombobox({
  value, search, onSearch, options, searching, onPick,
}: {
  value: AppuserHit | null
  search: string
  onSearch: (q: string) => void
  options: AppuserHit[]
  searching: boolean
  onPick: (hit: AppuserHit | null) => void
}) {
  const [focused, setFocused] = useState(false)
  return (
    <div className="relative">
      {value ? (
        <div className="flex items-center gap-2 rounded-control border border-border bg-white/5 px-2 py-1.5">
          <span className="truncate text-sm text-app-text">{value.label}</span>
          <button
            type="button"
            onClick={() => onPick(null)}
            className="ml-auto text-xs text-app-muted hover:text-status-error"
            aria-label="Clear appuser"
          >
            ×
          </button>
        </div>
      ) : (
        <Input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 120)}
          placeholder="source_key or email…"
          className="w-full"
        />
      )}
      {focused && !value && search.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-56 overflow-y-auto rounded-control border border-border bg-app-panel2 shadow-card">
          {searching ? (
            <div className="px-3 py-2 text-xs text-app-muted">Searching…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-app-muted">No matches.</div>
          ) : (
            options.map((o) => (
              <button
                key={o.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); onPick(o) }}
                className="block w-full px-3 py-1.5 text-left text-xs text-app-text hover:bg-white/5"
              >
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
